import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  X,
  Send,
  RefreshCw,
  AlertCircle,
  Minimize2,
  Maximize2
} from 'lucide-react';
import StreamingMessage from './StreamingMessage';
import { useGeminiStream } from '../hooks/useGeminiStream';
import { useResumeLoader } from '../hooks/useResumeLoader';
import { useProjectLoader } from '../hooks/useProjectLoader';
import { buildSystemPrompt, sanitizeResumeContent } from '../utils/promptBuilder';
import { Message, ChatbotProps } from '../types/chat';

const Chatbot: React.FC<ChatbotProps> = ({
  isOpen: controlledIsOpen,
  onClose,
  initialOpen = false,
  position = 'bottom-right'
}) => {
  // Support both controlled (isOpen/onClose from parent) and uncontrolled (initialOpen) modes
  const [internalOpen, setInternalOpen] = useState(initialOpen);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalOpen;
  const handleClose = () => {
    onClose ? onClose() : setInternalOpen(false);
  };

  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTimeRef = useRef(0);

  // Load resume content
  const {
    resumeContent,
    isLoading: isLoadingResume,
    error: resumeError,
    refreshResume
  } = useResumeLoader(5 * 60 * 1000); // Auto-refresh every 5 minutes

  // Load projects content
  const {
    projectsContent,
    isLoading: isLoadingProjects,
    error: projectsError,
    refreshProjects,
    loadedProjectsCount
  } = useProjectLoader();

  // Memoize sanitized resume — avoid running 8 regex ops on every message send
  const sanitizedResume = useMemo(
    () => resumeContent ? sanitizeResumeContent(resumeContent) : '',
    [resumeContent]
  );

  // Streaming handlers
  const handleStreamChunk = (chunk: string, messageId: string) => {
    setStreamingMessageId(messageId);
    setStreamingText(prev => prev + chunk);
  };

  const handleStreamComplete = (messageId: string, fullMessage: string, metadata?: any) => {
    setStreamingMessageId(null);
    setStreamingText('');

    // Add completed assistant message
    const assistantMessage: Message = {
      id: messageId,
      content: fullMessage,
      role: 'assistant',
      timestamp: new Date(),
      metadata
    };

    setMessages(prev => [...prev, assistantMessage]);
  };

  const handleStreamError = (error: string) => {
    setStreamingMessageId(null);
    setStreamingText('');

    // Add error message
    const errorMessage: Message = {
      id: `error-${Date.now()}`,
      content: `I apologize, but I encountered an error: ${error}. Please try again or ask a different question about Thanh's background.`,
      role: 'assistant',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, errorMessage]);
  };

  // Initialize Gemini stream
  const {
    isStreaming,
    isLoading: isLoadingGemini,
    error: geminiError,
    sendMessage,
    abortStream
  } = useGeminiStream(handleStreamChunk, handleStreamComplete, handleStreamError);

  // Throttled scroll — at most once per 150ms during streaming to avoid jank
  useEffect(() => {
    if (messages.length > 0 || isStreaming) {
      const now = Date.now();
      if (now - lastScrollTimeRef.current > 150) {
        lastScrollTimeRef.current = now;
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages, streamingText, isStreaming]);

  // Handle message send
  const handleSendMessage = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isStreaming || !resumeContent) return;

    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content: trimmedInput,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Build system prompt with memoized sanitized resume
    const systemPrompt = buildSystemPrompt(
      sanitizedResume,
      projectsContent || [],
      messages
    );

    // Send to Gemini
    await sendMessage(trimmedInput, systemPrompt, messages);
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Welcome message with project context
  const getWelcomeMessage = () => {
    const projectsInfo = loadedProjectsCount > 0
      ? ` I have detailed information about ${loadedProjectsCount} of his key projects including technical implementations, architectures, and real code examples.`
      : ' I\'m loading his project details to give you comprehensive insights.';

    return `👋 Hi! I'm Thanh's AI Assistant. I can help you learn about his background, skills, and experience.${projectsInfo} What would you like to know?`;
  };

  // Position classes
  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  };

  // When controlled from ChatBubble, don't render the standalone open button
  if (!isOpen && controlledIsOpen !== undefined) {
    return null;
  }

  if (!isOpen) {
    return (
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setInternalOpen(true)}
        className={`
          fixed ${positionClasses[position]} z-50
          w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-500
          text-white rounded-full shadow-lg hover:shadow-xl
          flex items-center justify-center
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
        `}
        aria-label="Open AI Assistant"
      >
        <MessageCircle size={24} />
      </motion.button>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
          height: isMinimized ? '60px' : '500px'
        }}
        exit={{ opacity: 0, scale: 0.8, y: 20 }}
        className={`
          fixed ${positionClasses[position]} z-50
          w-80 sm:w-96 bg-white/95 dark:bg-gray-900/95
          backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50
          rounded-2xl shadow-2xl
          flex flex-col overflow-hidden
          transition-all duration-300
        `}
        style={{
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <MessageCircle size={16} />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Thanh's AI Assistant</h3>
              <p className="text-xs opacity-75">
                {isLoadingResume || isLoadingProjects
                  ? 'Loading...'
                  : loadedProjectsCount > 0
                    ? `Ready • ${loadedProjectsCount} projects loaded`
                    : 'Ready to help'
                }
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
            </button>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Chat Content */}
        {!isMinimized && (
          <>
            {/* Error Display */}
            {(resumeError || projectsError || geminiError) && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                  <AlertCircle size={16} />
                  <span>{resumeError || projectsError || geminiError}</span>
                  <div className="ml-auto flex gap-1">
                    {resumeError && (
                      <button
                        onClick={refreshResume}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded"
                        title="Refresh resume"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                    {projectsError && (
                      <button
                        onClick={refreshProjects}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded"
                        title="Refresh projects"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
              style={{ maxHeight: '320px' }}
            >
              {/* Welcome Message */}
              {messages.length === 0 && !isStreaming && (
                <div className="text-center text-gray-600 dark:text-gray-400 text-sm py-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900 to-blue-900 rounded-full mx-auto mb-3 flex items-center justify-center">
                    <MessageCircle size={20} className="text-purple-600 dark:text-purple-400" />
                  </div>
                  <p className="leading-relaxed">{getWelcomeMessage()}</p>
                  {loadedProjectsCount > 0 && (
                    <div className="mt-3 text-xs opacity-75">
                      📋 Project details loaded: Mobile Testing, API Automation, Playwright Framework, Cypress Testing, Performance Testing
                    </div>
                  )}
                </div>
              )}

              {/* Message List */}
              {messages.map((message) => (
                <StreamingMessage
                  key={message.id}
                  message={message}
                />
              ))}

              {/* Streaming Message */}
              {isStreaming && streamingMessageId && (
                <StreamingMessage
                  message={{
                    id: streamingMessageId,
                    content: streamingText,
                    role: 'assistant',
                    timestamp: new Date()
                  }}
                  isStreaming={true}
                  streamingText={streamingText}
                />
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about Thanh's experience..."
                    disabled={isStreaming || isLoadingGemini || !resumeContent}
                    className="
                      w-full px-3 py-2 pr-10
                      bg-white dark:bg-gray-800
                      border border-gray-300 dark:border-gray-600
                      rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                      disabled:opacity-50 disabled:cursor-not-allowed
                      placeholder:text-gray-500 dark:placeholder:text-gray-400
                      text-gray-900 dark:text-gray-100
                    "
                  />
                  {isStreaming && (
                    <button
                      onClick={abortStream}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-red-500 hover:bg-red-50 rounded"
                      aria-label="Stop"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isStreaming || isLoadingGemini || !resumeContent}
                  className="
                    p-2 bg-gradient-to-r from-purple-500 to-blue-500
                    text-white rounded-lg shadow-sm
                    hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed
                    transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
                  "
                  aria-label="Send message"
                >
                  <Send size={16} />
                </button>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {isLoadingResume
                    ? 'Loading resume...'
                    : isLoadingProjects
                    ? 'Loading projects...'
                    : isLoadingGemini
                    ? 'Preparing response...'
                    : isStreaming
                    ? 'AI is typing...'
                    : `${messages.length} messages`
                  }
                </span>
                <div className="flex items-center gap-2">
                  {resumeContent && (
                    <span className="text-green-500">• Resume</span>
                  )}
                  {loadedProjectsCount > 0 && (
                    <span className="text-green-500">• {loadedProjectsCount} Projects</span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default Chatbot;
